"use client";

import { useQuery } from "@tanstack/react-query";
import Image from "next/image";

import { Skeleton } from "@/components/ui/skeleton";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { reportClientError } from "@/lib/observability/report-client-error";
import { suppressErrorReporting } from "@/lib/observability/report-error";
import {
  fetchWithTransientRetry,
  TRANSIENT_RETRY_STATUS_SET,
} from "@/lib/utils/fetch-with-transient-retry";
import type { PlatformId } from "@/types/platforms";

interface FeaturedAccount {
  userId: string;
  connectionId: string;
  platform: string;
  platformLabel: string;
  platformAccountId: string;
  platformUsername: string;
  imageUrl: string;
  followerCount: number | null;
}

// Error thrown by loadFeaturedAccounts when the final (post-retry) response is
// not ok. The captured `status` drives the downstream classification in the
// catch branch — relying on the HTTP status is safer than regex-matching the
// message because non-retryable 4xx responses share the same thrown shape.
class FeaturedAccountsLoadError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`Failed to load featured accounts (HTTP ${status})`);
    this.name = "FeaturedAccountsLoadError";
    this.status = status;
  }
}

async function loadFeaturedAccounts(): Promise<FeaturedAccount[]> {
  try {
    // SENTRY FIX: VIDSEEDS-2C5 — Failed to load featured accounts
    // SENTRY FIX: VIDSEEDS-2RR — Failed to load featured accounts
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: GKE ingress returns 503 during rolling-deploy windows; the
    // raw fetch immediately surfaced the transient 5xx as a hard error.
    // Fix: retry transient 5xx/408/429 a few times before failing.
    const response = await fetchWithTransientRetry(
      "/api/showcase/featured-accounts/",
      { cache: "no-store" },
    );

    if (!response.ok) {
      throw new FeaturedAccountsLoadError(response.status);
    }

    const payload = (await response.json()) as {
      accounts?: FeaturedAccount[];
    };
    return payload.accounts ?? [];
  } catch (error) {
    // Network drops on mobile devices fire TypeError: Failed to fetch.
    // The UI handles this gracefully (returns null), React Query retries,
    // and refetchOnReconnect restores data — downgrade to warning (NODE-220).
    const isNetworkError =
      error instanceof TypeError &&
      /Failed to fetch|Load failed/i.test(error.message);
    // SENTRY FIX: VIDSEEDS-2RR — Failed to load featured accounts (post-retry).
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: Crawlers (Googlebot/facebookexternalhit/Baiduspider) crawl
    //   localized marketing pages (/yo/, /sd/, /ig/product, …) and hit
    //   /api/showcase/featured-accounts during rolling-deploy windows. GKE
    //   ingress returns 503 while pods rotate; fetchWithTransientRetry
    //   exhausts 3 retries, throws, and the report landed a second Sentry
    //   issue on top of the tracked-fetch 503 classification — duplicate
    //   noise. User impact is zero: FeaturedCreators returns null on
    //   isError so the block is simply absent from the SSR output.
    // Fix (v2 after Seer review): classify as post-retry-transient ONLY
    //   when the captured HTTP status was retryable (5xx/408/425/429/520-527).
    //   Non-retryable 4xx responses fall through to reportClientError so a
    //   genuine 400/401/403/404 on /api/showcase/featured-accounts keeps
    //   its regular error-level Sentry issue.
    const isPostRetryTransient =
      error instanceof FeaturedAccountsLoadError &&
      TRANSIENT_RETRY_STATUS_SET.has(error.status);
    if (isPostRetryTransient) {
      const transientError = error as FeaturedAccountsLoadError;
      suppressErrorReporting(transientError.message, {
        source: "components.FeaturedCreators",
        feature: "homepage",
        action: "loadFeaturedAccounts",
        expected: true,
        extra: {
          postRetryTransient: true,
          status: transientError.status,
        },
      });
    } else {
      reportClientError(error, {
        source: "components.FeaturedCreators",
        feature: "homepage",
        action: "loadFeaturedAccounts",
        ...(isNetworkError && { severity: "warning" }),
        extra:
          error instanceof FeaturedAccountsLoadError
            ? { status: error.status }
            : undefined,
      });
    }
    throw error;
  }
}

function formatFollowers(count: number | null): string | null {
  if (typeof count !== "number" || Number.isNaN(count)) {
    return null;
  }

  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(count);
}

function normalizeHandle(value: string | null): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim().replace(/^@+/, "");
  if (trimmed.length === 0 || /\s/.test(trimmed)) {
    return null;
  }

  return trimmed;
}

function getProfileUrl(account: FeaturedAccount): string | null {
  const platform = account.platform as PlatformId;
  const encodedAccountId = encodeURIComponent(account.platformAccountId);

  switch (platform) {
    case "youtube":
      return `https://www.youtube.com/channel/${encodedAccountId}`;
    case "facebook":
      return `https://www.facebook.com/${encodedAccountId}`;
    case "instagram": {
      const handle = normalizeHandle(account.platformUsername);
      return handle ? `https://www.instagram.com/${handle}/` : null;
    }
    case "tiktok": {
      const handle = normalizeHandle(account.platformUsername);
      return handle ? `https://www.tiktok.com/@${handle}` : null;
    }
    case "x": {
      const handle = normalizeHandle(account.platformUsername);
      return handle ? `https://x.com/${handle}` : null;
    }
    case "linkedin": {
      const organizationId = account.platformAccountId.match(
        /^urn:li:organization:(.+)$/,
      )?.[1];
      return organizationId
        ? `https://www.linkedin.com/company/${encodeURIComponent(organizationId)}/`
        : null;
    }
    default:
      return null;
  }
}

export function FeaturedCreators() {
  const { t } = useJsonTranslation("HomepageFeaturedCreators");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["homepage-featured-accounts"],
    queryFn: loadFeaturedAccounts,
    staleTime: 60 * 60 * 1000,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="relative overflow-hidden px-5 py-6 sm:px-8 sm:py-8">
        <div className="space-y-8">
          <div className="space-y-3 text-center">
            <Skeleton className="bg-muted/70 mx-auto h-4 w-48 rounded-full" />
            <Skeleton className="bg-muted/70 mx-auto h-10 w-96 max-w-full rounded-full" />
            <Skeleton className="bg-muted/70 mx-auto h-4 w-[32rem] max-w-full rounded-full" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="bg-card/70 h-24 rounded-3xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const accounts = data ?? [];
  if (accounts.length === 0) {
    return null;
  }

  const marqueeAccounts =
    accounts.length > 1 ? [...accounts, ...accounts] : accounts;

  return (
    <div
      data-home-block="featured-creators"
      className="home-showcase-panel px-5 py-7 sm:px-8 sm:py-9"
    >
      <div className="relative space-y-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="home-eyebrow">
            {t("eyebrow")}
          </p>
          <h2 className="home-section-title mt-4 text-3xl sm:text-4xl">
            {t("title")}
          </h2>
          <p className="home-section-copy mt-4 text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </div>

        <div className="relative overflow-x-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-[var(--background)] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-[var(--background)] to-transparent" />

          <div
            className={
              accounts.length > 1
                ? "homepage-marquee-track flex w-max gap-4 py-2"
                : "flex justify-center py-2"
            }
          >
            {marqueeAccounts.map((account, index) => {
              const followers = formatFollowers(account.followerCount);
              const profileUrl = getProfileUrl(account);
              const cardClassName =
                "uni-card group flex min-w-[18rem] items-center gap-4";

              const cardContent = (
                <>
                  <div className="border-border relative h-14 w-14 overflow-hidden rounded-full border">
                    <Image
                      src={account.imageUrl}
                      alt={account.platformUsername}
                      fill
                      className="object-cover"
                      sizes="56px"
                      unoptimized
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-base font-semibold">
                      {account.platformUsername}
                    </p>
                    <div className="text-muted-foreground mt-1 flex items-center gap-2 text-sm">
                      <span>{account.platformLabel}</span>
                      {followers ? <span>• {followers}</span> : null}
                    </div>
                  </div>
                </>
              );

              if (!profileUrl) {
                return (
                  <div
                    key={`${account.connectionId}-${index}`}
                    className={cardClassName}
                  >
                    {cardContent}
                  </div>
                );
              }

              return (
                <a
                  key={`${account.connectionId}-${index}`}
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${cardClassName} focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none`}
                >
                  {cardContent}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
